/**
 * 外見編集モーダル（保護者ページ用）
 *
 * メガネ、髪型、体型、顔の形を一括編集
 */
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useState, useEffect, useRef } from "react";
import { Dialog } from "@mui/material";
import { useDialog } from "app/hook";
import { DialogType } from "app/types";
import { AppDispatch } from "app/store";
import { useScale, usePlaySound } from "common/utils";
import { ParentCloseBtn, FadeTransition, Select } from "common/elements";
import I18n from "features/parent/i18n";
import { updateUser } from "features/user/api";

// 保護者ページ用の外見オプション（漢字バージョン）
const PARENT_ACCESSORY_OPTIONS = [
  { key: "none", icon: "block", labelKey: "MSG_ACCESSORY_NONE_BY_PARENT" },
  { key: "megane", icon: "eyeglasses", labelKey: "MSG_ACCESSORY_MEGANE_BY_PARENT" },
  { key: "boushi", icon: "school", labelKey: "MSG_ACCESSORY_BOUSHI_BY_PARENT" },
  { key: "hairpin", icon: "spa", labelKey: "MSG_ACCESSORY_HAIRPIN_BY_PARENT" },
  {
    key: "ribbon",
    icon: "featured_seasonal_and_gifts",
    labelKey: "MSG_ACCESSORY_RIBBON_BY_PARENT",
  },
  { key: "bandaid", icon: "healing", labelKey: "MSG_ACCESSORY_BANDAID_BY_PARENT" },
];

const PARENT_HAIRSTYLE_OPTIONS = [
  { key: "very_short", labelKey: "MSG_HAIRSTYLE_VERY_SHORT_BY_PARENT" },
  { key: "short", labelKey: "MSG_HAIRSTYLE_SHORT_BY_PARENT" },
  { key: "medium", labelKey: "MSG_HAIRSTYLE_MEDIUM_BY_PARENT" },
  { key: "slightly_long", labelKey: "MSG_HAIRSTYLE_SLIGHTLY_LONG_BY_PARENT" },
  { key: "long", labelKey: "MSG_HAIRSTYLE_LONG_BY_PARENT" },
];

const PARENT_BODY_TYPE_OPTIONS = [
  { key: "thin", labelKey: "MSG_BODY_TYPE_THIN_BY_PARENT" },
  { key: "slightly_thin", labelKey: "MSG_BODY_TYPE_SLIGHTLY_THIN_BY_PARENT" },
  { key: "medium", labelKey: "MSG_BODY_TYPE_MEDIUM_BY_PARENT" },
  { key: "slightly_chubby", labelKey: "MSG_BODY_TYPE_SLIGHTLY_CHUBBY_BY_PARENT" },
  { key: "chubby", labelKey: "MSG_BODY_TYPE_CHUBBY_BY_PARENT" },
];

const PARENT_FACE_SHAPE_OPTIONS = [
  { key: "round", labelKey: "MSG_FACE_SHAPE_ROUND_BY_PARENT" },
  { key: "oval", labelKey: "MSG_FACE_SHAPE_OVAL_BY_PARENT" },
  { key: "oblong", labelKey: "MSG_FACE_SHAPE_OBLONG_BY_PARENT" },
  { key: "square", labelKey: "MSG_FACE_SHAPE_SQUARE_BY_PARENT" },
  { key: "triangle", labelKey: "MSG_FACE_SHAPE_TRIANGLE_BY_PARENT" },
];

interface AppearanceEditModalProps {
  open: boolean;
  uid: string;
  currentAccessory: string[] | null;
  currentHairstyle: string | null;
  currentBodyType: string | null;
  currentFaceShape: string | null;
  onClose: () => void;
}

export const AppearanceEditModal = ({
  open,
  uid,
  currentAccessory,
  currentHairstyle,
  currentBodyType,
  currentFaceShape,
  onClose,
}: AppearanceEditModalProps) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const handleDialog = useDialog();
  const dispatch = useDispatch<AppDispatch>();

  // 一時状態
  const [accessory, setAccessory] = useState<string[]>([]);
  const [hairstyle, setHairstyle] = useState<string>("");
  const [bodyType, setBodyType] = useState<string>("");
  const [faceShape, setFaceShape] = useState<string>("");
  const [disable, setDisable] = useState(false);

  // Select開閉状態
  const [hairstyleOpen, setHairstyleOpen] = useState(false);
  const [bodyTypeOpen, setBodyTypeOpen] = useState(false);
  const [faceShapeOpen, setFaceShapeOpen] = useState(false);

  // Select refs
  const hairstyleRef = useRef<HTMLButtonElement>(null);
  const bodyTypeRef = useRef<HTMLButtonElement>(null);
  const faceShapeRef = useRef<HTMLButtonElement>(null);

  // モーダルが開いた時に現在値を設定
  useEffect(() => {
    if (open) {
      setAccessory(currentAccessory || []);
      setHairstyle(currentHairstyle || "");
      setBodyType(currentBodyType || "");
      setFaceShape(currentFaceShape || "");
      setDisable(false);
    }
  }, [open, currentAccessory, currentHairstyle, currentBodyType, currentFaceShape]);

  // バリデーション：すべてのフィールドが選択されているか（accessoryは1つ以上必須）
  const isValid = accessory.length > 0 && hairstyle && bodyType && faceShape;

  // アクセサリー選択トグル（排他制御あり）
  const handleAccessoryToggle = (key: string) => {
    play();

    // 「なし」を選択した場合：他をすべて解除して「なし」のみにする
    if (key === "none") {
      if (accessory.includes("none")) {
        setAccessory([]);
      } else {
        setAccessory(["none"]);
      }
      return;
    }

    // 他のアクセサリーを選択した場合：「なし」を解除
    const withoutNone = accessory.filter((a) => a !== "none");
    if (withoutNone.includes(key)) {
      setAccessory(withoutNone.filter((a) => a !== key));
    } else {
      setAccessory([...withoutNone, key]);
    }
  };

  const handleConfirm = () => {
    if (disable) return;
    setDisable(true);
    play();

    handleDialog({ type: DialogType.LOADING, value: true });

    const userInfo = {
      accessory: accessory.length > 0 ? accessory : null,
      hairstyle: hairstyle || null,
      body_type: bodyType || null,
      face_shape: faceShape || null,
    };

    dispatch(updateUser({ uid, userInfo }))
      .unwrap()
      .then(() => {
        toast(
          <div>
            <p className="text text-textcolor-black">{I18n.t("MSG_TOAST_EDIT_SUCCESS")}</p>
          </div>,
        );
        onClose();
      })
      .catch((error) => {
        console.error(error);
        setDisable(false);
      })
      .finally(() => handleDialog({ type: DialogType.LOADING, value: false }));
  };

  const selectBtnClass =
    "flex items-center justify-between gap-2 py-3 px-4 rounded-[8px] text-[18px] cursor-pointer select-none bg-white border-2 border-green-alt text-textcolor-black w-[280px]";

  return (
    <Dialog
      fullScreen
      open={open}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        <div style={{ transform: `scale(${scale})` }}>
          <div className="flex-col-el w-[600px] border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30">
            <div className="flex-col-el justify-start w-full h-full relative p-6 rounded-[8px] bg-white">
              <ParentCloseBtn close={onClose} />
              {/* ヘッダー */}
              <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline shrink-0">
                <span className="material-symbols-outlined !text-[54px] text-red-100">face</span>
                <h2 className="text-[33px] text-textcolor-black">
                  {I18n.t("MSG_PARENT_PAGE_TAB_APPEARANCE")}
                </h2>
              </div>

              {/* コンテンツ */}
              <div className="flex-col-el py-4 gap-4">
                {/* みにつけるもの（複数選択） */}
                <div className="flex-row-el items-center justify-between w-full py-3 px-4 bg-gray2-20 rounded-[12px]">
                  <p className="text-body text-textcolor-black text-[20px] w-[140px] shrink-0">
                    {I18n.t("MSG_ACCESSORY_TITLE_BY_PARENT")}
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {PARENT_ACCESSORY_OPTIONS.map((option) => {
                      const isActive = accessory.includes(option.key);
                      return (
                        <div
                          key={option.key}
                          onClick={() => handleAccessoryToggle(option.key)}
                          className={`
                            flex-col-el flex-center relative
                            w-[100px] h-[80px]
                            rounded-[8px]
                            cursor-pointer select-none
                            ${isActive ? "bg-green-alt border-2 border-green-100" : "bg-white border-2 border-gray2-40"}
                          `}
                        >
                          {isActive && (
                            <span className="absolute top-1 right-1 material-symbols-outlined text-[14px] text-green-100">
                              check_circle
                            </span>
                          )}
                          <span
                            className={`material-symbols-outlined !text-[24px] mb-1 ${isActive ? "text-green-100" : "text-gray2-60"}`}
                          >
                            {option.icon}
                          </span>
                          <span
                            className={`text-body text-[14px] text-center ${isActive ? "text-textcolor-black" : "text-textcolor-black/70"}`}
                          >
                            {I18n.t(option.labelKey)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* 髪型 */}
                <div className="flex-row-el items-center justify-between w-full py-3 px-4 bg-gray2-20 rounded-[12px]">
                  <p className="text-body text-textcolor-black text-[20px] w-[140px] shrink-0">
                    {I18n.t("MSG_HAIRSTYLE_TITLE_BY_PARENT")}
                  </p>
                  <button
                    ref={hairstyleRef}
                    onClick={() => {
                      play();
                      setHairstyleOpen(true);
                    }}
                    className={selectBtnClass}
                  >
                    <span>
                      {hairstyle
                        ? I18n.t(
                            PARENT_HAIRSTYLE_OPTIONS.find((o) => o.key === hairstyle)?.labelKey ||
                              "",
                          )
                        : I18n.t("MSG_SELECT_PLACEHOLDER_BY_PARENT")}
                    </span>
                    <span className="material-symbols-outlined !text-[24px]">expand_more</span>
                  </button>
                  <Select
                    anchorRef={hairstyleRef}
                    open={hairstyleOpen}
                    onClose={() => setHairstyleOpen(false)}
                    value={hairstyle}
                    onChange={(v: string) => setHairstyle(v)}
                    options={PARENT_HAIRSTYLE_OPTIONS.map((o) => ({
                      value: o.key,
                      label: I18n.t(o.labelKey),
                    }))}
                  />
                </div>

                {/* 体型 */}
                <div className="flex-row-el items-center justify-between w-full py-3 px-4 bg-gray2-20 rounded-[12px]">
                  <p className="text-body text-textcolor-black text-[20px] w-[140px] shrink-0">
                    {I18n.t("MSG_BODY_TYPE_TITLE_BY_PARENT")}
                  </p>
                  <button
                    ref={bodyTypeRef}
                    onClick={() => {
                      play();
                      setBodyTypeOpen(true);
                    }}
                    className={selectBtnClass}
                  >
                    <span>
                      {bodyType
                        ? I18n.t(
                            PARENT_BODY_TYPE_OPTIONS.find((o) => o.key === bodyType)?.labelKey ||
                              "",
                          )
                        : I18n.t("MSG_SELECT_PLACEHOLDER_BY_PARENT")}
                    </span>
                    <span className="material-symbols-outlined !text-[24px]">expand_more</span>
                  </button>
                  <Select
                    anchorRef={bodyTypeRef}
                    open={bodyTypeOpen}
                    onClose={() => setBodyTypeOpen(false)}
                    value={bodyType}
                    onChange={(v: string) => setBodyType(v)}
                    options={PARENT_BODY_TYPE_OPTIONS.map((o) => ({
                      value: o.key,
                      label: I18n.t(o.labelKey),
                    }))}
                  />
                </div>

                {/* 顔の形 */}
                <div className="flex-row-el items-center justify-between w-full py-3 px-4 bg-gray2-20 rounded-[12px]">
                  <p className="text-body text-textcolor-black text-[20px] w-[140px] shrink-0">
                    {I18n.t("MSG_FACE_SHAPE_TITLE_BY_PARENT")}
                  </p>
                  <button
                    ref={faceShapeRef}
                    onClick={() => {
                      play();
                      setFaceShapeOpen(true);
                    }}
                    className={selectBtnClass}
                  >
                    <span>
                      {faceShape
                        ? I18n.t(
                            PARENT_FACE_SHAPE_OPTIONS.find((o) => o.key === faceShape)?.labelKey ||
                              "",
                          )
                        : I18n.t("MSG_SELECT_PLACEHOLDER_BY_PARENT")}
                    </span>
                    <span className="material-symbols-outlined !text-[24px]">expand_more</span>
                  </button>
                  <Select
                    anchorRef={faceShapeRef}
                    open={faceShapeOpen}
                    onClose={() => setFaceShapeOpen(false)}
                    value={faceShape}
                    onChange={(v: string) => setFaceShape(v)}
                    options={PARENT_FACE_SHAPE_OPTIONS.map((o) => ({
                      value: o.key,
                      label: I18n.t(o.labelKey),
                    }))}
                  />
                </div>
              </div>

              {/* ボタン */}
              <div className="flex-row-el flex-center gap-4 mt-4">
                <button
                  onClick={onClose}
                  className="flex-row-el items-center justify-center w-[160px] py-3 bg-white border border-gray2-60 text-textcolor-black rounded-[12px] text-[24px] cursor-pointer hover:bg-gray2-20 transition-all"
                >
                  {I18n.t("MSG_CANCEL_BTN_BY_PARENT")}
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={disable || !isValid}
                  className={`flex-row-el items-center justify-center w-[160px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] transition-all ${
                    disable || !isValid
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer hover:opacity-90"
                  }`}
                >
                  {I18n.t("MSG_CONFIRM_BTN_BY_PARENT")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
